import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { HelpCircle, Bug, Lightbulb, MessageCircle, MoreHorizontal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackModal = ({ open, onClose }) => {
    const [type, setType] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const feedbackTypes = [
        { value: 'bug', label: 'Bug Report', icon: Bug, color: '#EF4444' },
        { value: 'feature', label: 'Feature Request', icon: Lightbulb, color: '#3B82F6' },
        { value: 'question', label: 'Question', icon: MessageCircle, color: '#10B981' },
        { value: 'other', label: 'Other', icon: MoreHorizontal, color: '#6B7280' },
    ];

    const handleSubmit = async () => {
        if (!type) {
            toast.error('Please select a feedback type');
            return;
        }
        if (!message.trim()) {
            toast.error('Please enter your message');
            return;
        }

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('chatAppToken');
            await axios.post(`${API}/feedback`, {
                type,
                message: message.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Feedback submitted! Thank you 🙏');
            setType('');
            setMessage('');
            onClose();
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            toast.error('Failed to submit feedback');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setType('');
        setMessage('');
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                className="sm:max-w-md"
                style={{
                    backgroundColor: '#1e1e1e',
                    border: '1px solid rgba(255, 200, 87, 0.2)',
                    color: '#F5E3D7'
                }}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ color: '#FFC857' }}>
                        <HelpCircle className="w-5 h-5" />
                        Help & Feedback
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Type Selector */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>
                            Type
                        </label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger
                                style={{
                                    backgroundColor: '#2a2a2a',
                                    border: '1px solid rgba(255, 200, 87, 0.2)',
                                    color: '#F5E3D7'
                                }}
                            >
                                <SelectValue placeholder="Select feedback type" />
                            </SelectTrigger>
                            <SelectContent
                                style={{
                                    backgroundColor: '#2a2a2a',
                                    border: '1px solid rgba(255, 200, 87, 0.2)'
                                }}
                            >
                                {feedbackTypes.map((ft) => (
                                    <SelectItem
                                        key={ft.value}
                                        value={ft.value}
                                        style={{ color: '#F5E3D7' }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <ft.icon className="w-4 h-4" style={{ color: ft.color }} />
                                            {ft.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium" style={{ color: '#F5E3D7' }}>
                            Your Message
                        </label>
                        <Textarea
                            placeholder="Describe your issue or suggestion..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            style={{
                                backgroundColor: '#2a2a2a',
                                border: '1px solid rgba(255, 200, 87, 0.2)',
                                color: '#F5E3D7',
                                resize: 'none'
                            }}
                        />
                    </div>
                </div>

                {/* Submit Button */}
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full font-semibold"
                    style={{
                        background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)',
                        color: '#47030F'
                    }}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default FeedbackModal;
